/**
 * GeoJSON data cache and preloader
 * Ensures all geographic boundary files are loaded before address searches
 */

const GEOJSON_FILES = [
  'VCIRTA.geojson',
  'Kootenay_Boundary.geojson',
  'SIT_Municipalities.geojson',
  'SIT.geojson',
  'Municipalities_VCIRTA.geojson',
  'RFT_Boundary.geojson',
];

class GeoDataCache {
  constructor() {
    this.cache = new Map();
    this.loadingPromise = null;
    this.isLoaded = false;
    this.loadErrors = [];
  }

  /**
   * Fetch a single GeoJSON file with retry logic
   */
  async fetchWithRetry(url, retries = 2, delay = 500) {
    for (let i = 0; i <= retries; i++) {
      try {
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        return await res.json();
      } catch (error) {
        if (i === retries) {
          throw error;
        }
        console.warn(`Retry ${i + 1}/${retries} for ${url}:`, error.message);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Preload all GeoJSON files into cache
   * Returns a promise that resolves when all files are loaded
   */
  async preload() {
    // If already loading, return the existing promise
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // If already loaded, return immediately
    if (this.isLoaded) {
      return Promise.resolve();
    }

    this.loadingPromise = (async () => {
      console.log('Preloading GeoJSON files...');
      this.loadErrors = [];

      const results = await Promise.allSettled(
        GEOJSON_FILES.map(async (file) => {
          try {
            const data = await this.fetchWithRetry(`/json/geo/${file}`);
            this.cache.set(file, data);
            console.log(`✓ Loaded ${file}`);
            return { file, success: true };
          } catch (error) {
            this.loadErrors.push({ file, error: error.message });
            console.error(`✗ Failed to load ${file}:`, error.message);
            return { file, success: false, error };
          }
        })
      );

      const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      
      if (successCount === 0) {
        throw new Error('Failed to load any GeoJSON files');
      }

      if (this.loadErrors.length > 0) {
        console.warn(`Loaded ${successCount}/${GEOJSON_FILES.length} GeoJSON files with errors:`, this.loadErrors);
      } else {
        console.log(`✓ All ${GEOJSON_FILES.length} GeoJSON files loaded successfully`);
      }

      this.isLoaded = true;
      return { successCount, totalCount: GEOJSON_FILES.length, errors: this.loadErrors };
    })();

    return this.loadingPromise;
  }

  /**
   * Get a cached GeoJSON file
   * Returns null if not in cache
   */
  get(filename) {
    return this.cache.get(filename) || null;
  }

  /**
   * Check if all files are loaded
   */
  isReady() {
    return this.isLoaded;
  }

  /**
   * Get all cached files
   */
  getAll() {
    return Array.from(this.cache.entries()).map(([file, data]) => ({
      file,
      data
    }));
  }

  /**
   * Get loading status
   */
  getStatus() {
    return {
      isLoaded: this.isLoaded,
      isLoading: !!this.loadingPromise && !this.isLoaded,
      cachedCount: this.cache.size,
      totalCount: GEOJSON_FILES.length,
      errors: this.loadErrors
    };
  }
}

// Create singleton instance
const geoDataCache = new GeoDataCache();

export default geoDataCache;
